import { ADMIN_PRINCIPAL_ID, AllowedPermissions, UNKNOWN_ENUM_VALUE, } from '@lucidlink/dto/validation';
import { FilespacePrincipalResponseDto } from '@lucidlink/dto/web-service-api';
import { arrayToPrivateKey, decryptWithRsaBlockCipher, encryptionKeyPairFromPrivateKey, exportPrivateCryptoKeyToPem, RSA_ENCRYPTION_ALGO_PARAMS, } from '@lucidlink/ts-crypto/crypto';
import { assertCanHandle } from '@lucidlink/tscore';
import { createIdService, } from '@lucidlink/web-fs/auth/id-service';
import { UnauthorizedError as WebFsUnauthorizedError, UnknownError as WebFsUnknownError, } from '@lucidlink/web-fs/common';
import { createFilespace, FilespaceState, } from '@lucidlink/web-fs/filespace';
import { parserFromDto } from '#fetcher';
import { HttpResponseError, UnauthorizedError as FetcherUnauthorizedError, } from '#fetcher/fetch-error.types';
import { TimeCachedValue } from '#sdk/utils/time-cached-value';
import { NotLoggedInToFilespaceError } from '#session/session-manager';
import { createDirectLinks } from './direct-links';
import { createEventsManager } from './events-manager';
import { createExternalFilesManager } from './external-files-manager';
import { createFilePermissionsManager } from './file-permissions-manager';
import { createFileSystem } from './file-system';
import { createGeneralSettingManager } from './general-settings-manager';
import { createGlobalSettingManager } from './global-setting-manager';
import { createGuestLinksManager } from './guest-links-manager';
import { createSnapshotManager } from './snapshot-manager';
import { createSnapshotSchedules } from './snapshot-schedules';
export const createFilespaceClient = ({ logger, workspaceId, filespaceId, legacyFilespaceName, principalId, principalName, memberCrypto, adminCrypto, principalsCrypto, getFreshToken, getTelemetryClient, membersMap, groupsMap, serviceAccountsMap, hubEndpoint, filespaceFetcher, additionalInfo, onFilespaceLogout, loginFlags, }) => {
    return new FilespaceClientImpl(logger, workspaceId, filespaceId, legacyFilespaceName, principalId, principalName, memberCrypto, adminCrypto, principalsCrypto, membersMap, groupsMap, serviceAccountsMap, getTelemetryClient, getFreshToken, hubEndpoint, filespaceFetcher, additionalInfo, onFilespaceLogout, loginFlags);
};
export class FilespaceClientImpl {
    logger;
    workspaceId;
    filespaceId;
    legacyFilespaceName;
    principalId;
    principalName;
    memberCrypto;
    adminCrypto;
    principalsCrypto;
    membersMap;
    groupsMap;
    serviceAccountsMap;
    getTelemetryClient;
    getFreshToken;
    hubEndpoint;
    filespaceFetcher;
    onFilespaceLogout;
    loginFlags;
    filespace;
    guestLinksManager = undefined;
    constructor(logger, workspaceId, filespaceId, legacyFilespaceName, principalId, principalName, memberCrypto, adminCrypto, principalsCrypto, membersMap, groupsMap, serviceAccountsMap, getTelemetryClient, getFreshToken, hubEndpoint, filespaceFetcher, additionalInfo, onFilespaceLogout, loginFlags) {
        this.logger = logger;
        this.workspaceId = workspaceId;
        this.filespaceId = filespaceId;
        this.legacyFilespaceName = legacyFilespaceName;
        this.principalId = principalId;
        this.principalName = principalName;
        this.memberCrypto = memberCrypto;
        this.adminCrypto = adminCrypto;
        this.principalsCrypto = principalsCrypto;
        this.membersMap = membersMap;
        this.groupsMap = groupsMap;
        this.serviceAccountsMap = serviceAccountsMap;
        this.getTelemetryClient = getTelemetryClient;
        this.getFreshToken = getFreshToken;
        this.hubEndpoint = hubEndpoint;
        this.filespaceFetcher = filespaceFetcher;
        this.onFilespaceLogout = onFilespaceLogout;
        this.loginFlags = loginFlags;
        const logTelemetry = (event) => {
            this.logFilespaceEvent(event);
        };
        this.filespace = createFilespace({
            application: additionalInfo.application,
            platformInfo: {
                platform: additionalInfo.platform,
                platformVersion: additionalInfo.platformVersion,
                webBrowser: additionalInfo.webBrowser,
            },
            logTelemetry: logTelemetry,
        });
    }
    getFilespaceId() {
        return this.filespaceId;
    }
    getWorkspaceId() {
        return this.workspaceId;
    }
    getLogLevel(name) {
        return this.filespace.getLogLevel(name);
    }
    setLogLevel(level, name) {
        return this.filespace.setLogLevel(level, name);
    }
    async link() {
        this.currentPrincipal.prefetch();
        const memberCrypto = await this.memberCrypto.get();
        const memberKeys = await memberCrypto.verifyAndGetCurrentMemberKeys();
        const decryptedPrincipalPrivateKeyPem = await exportPrivateCryptoKeyToPem(memberKeys.encryptPair.privateKey);
        const decryptedWorkspaceCommonKey = await memberCrypto.getWorkspaceCommonKeyArray();
        const linkParams = {
            accessToken: '',
            decryptedWorkspaceCommonKey: new Uint8Array(decryptedWorkspaceCommonKey),
            workspaceId: this.workspaceId,
            filespaceId: this.filespaceId,
            legacyFilespaceName: this.legacyFilespaceName,
            principalId: this.principalId,
            principalName: this.principalName,
            decryptedPrincipalPrivateKeyPem,
        };
        const getFreshToken = () => executeGuardedAndRethrow(() => {
            return this.getFreshToken(false);
        });
        const getCurrentPrincipalInfo = () => executeGuardedAndRethrow(this.currentPrincipal.get);
        const memberAccess = this.createMemberAccess();
        const idService = createIdService(memberAccess, getFreshToken, getCurrentPrincipalInfo, this.principalCryptoFetcher, this.onFilespaceLogout);
        const linkSettings = this.loginFlags?.readOnlyMode !== undefined
            ? { readOnlyMode: this.loginFlags.readOnlyMode }
            : undefined;
        await this.filespace.link(idService, this.hubEndpoint, linkParams, linkSettings);
    }
    async unlink() {
        await this.filespace.unlink();
        this._filePermissionsManager = undefined;
    }
    fileSystem(snapshotId) {
        return createFileSystem(this.filespace.fileSystem(snapshotId));
    }
    externalFilesManager() {
        return createExternalFilesManager(this.logger, this.filespace.externalFilesManager());
    }
    eventsManager() {
        return createEventsManager(this.filespace.events(), this.workspaceId, this.filespaceId);
    }
    _filePermissionsManager = undefined;
    filePermissionsManager() {
        if (!this._filePermissionsManager) {
            this._filePermissionsManager = createFilePermissionsManager(this.filespace.filePermissionsManager(), this.membersMap, this.groupsMap, this.serviceAccountsMap, this.principalsCrypto);
        }
        return this._filePermissionsManager;
    }
    snapshotManager() {
        return createSnapshotManager(this.filespace.snapshotManager());
    }
    snapshotSchedules() {
        return createSnapshotSchedules(this.filespace.snapshotScheduler());
    }
    globalSettingManager() {
        return createGlobalSettingManager(this.filespace.configManager());
    }
    generalSettingsManager() {
        return createGeneralSettingManager(this.filespaceFetcher);
    }
    directLinks() {
        return createDirectLinks(this.filespace.directLinks());
    }
    logFilespaceEvent(event) {
        if (this.filespace.status().state === FilespaceState.UNLINKED) {
            throw new NotLoggedInToFilespaceError(this.filespaceId);
        }
        this.getTelemetryClient().sendEvent(event);
    }
    filespaceState() {
        return this.filespace.status().state;
    }
    stats() {
        return this.filespace.stats();
    }
    isTracing() {
        return this.filespace.isTracing();
    }
    recordTrace() {
        this.filespace.recordTrace();
    }
    stopTrace() {
        return this.filespace.stopTrace();
    }
    guestLinksManagement() {
        if (!this.guestLinksManager) {
            this.guestLinksManager = createGuestLinksManager({
                workspaceId: this.workspaceId,
                filespaceId: this.filespaceId,
                getAdminCrypto: this.adminCrypto.get,
                filespaceFetcher: this.filespaceFetcher,
                filePermissionsManager: this.filePermissionsManager(),
                principalsCryptoRefresh: () => this.principalsCrypto.refresh(),
                sendTelemetryEvent: (event) => this.getTelemetryClient().sendEvent(event),
            });
        }
        return this.guestLinksManager;
    }
    principalCryptoFetcher = async (principalId) => {
        const principalsCrypto = await this.principalsCrypto.get();
        const principal = await principalsCrypto.verifyAndGetPrincipalKeysSafe(principalId);
        return principal ?? undefined;
    };
    currentPrincipal = new TimeCachedValue(async () => {
        const { principalId, groupCryptos, isAdmin, allowedPermissions } = await this.filespaceFetcher.get('principals/me', {
            parser: parserFromDto(FilespacePrincipalResponseDto),
        });
        const memberCrypto = await this.memberCrypto.get();
        const memberKeys = await memberCrypto.verifyAndGetCurrentMemberKeys();
        const decryptedKeys = await Promise.all(groupCryptos.map(async ({ keyChain, principalId, }) => {
            const groupPrivateKey = await this.decryptPrivateKeyCryptoChain(memberKeys.encryptPair.privateKey, keyChain);
            return [principalId, groupPrivateKey];
        }));
        return {
            principalId,
            groupPrivateKeys: Object.fromEntries(decryptedKeys),
            isAdmin,
            allowedPermissions: allowedPermissions !== UNKNOWN_ENUM_VALUE
                ? allowedPermissions
                : AllowedPermissions.NONE,
        };
    });
    createMemberAccess() {
        const filespaceRootKeys = new TimeCachedValue(async () => {
            await this.adminCrypto.get();
            const currentPrincipal = await this.currentPrincipal.get();
            const filespaceRootPrivateKey = currentPrincipal.groupPrivateKeys[ADMIN_PRINCIPAL_ID];
            if (!filespaceRootPrivateKey) {
                throw new Error('Not an Admin');
            }
            return await encryptionKeyPairFromPrivateKey(filespaceRootPrivateKey);
        });
        return {
            getCryptoKeys: async () => {
                const memberCrypto = await this.memberCrypto.get();
                const memberKeys = await memberCrypto.verifyAndGetCurrentMemberKeys();
                return memberKeys.encryptPair;
            },
            getAdminCryptoKeys: filespaceRootKeys.refresh,
            getId: () => {
                return this.principalId;
            },
        };
    }
    async decryptPrivateKeyCryptoChain(memberPrivateKey, cryptoChain) {
        let currentKey = memberPrivateKey;
        for (const key of cryptoChain) {
            const privateKeyArray = await decryptWithRsaBlockCipher(key, currentKey);
            currentKey = await arrayToPrivateKey(privateKeyArray, RSA_ENCRYPTION_ALGO_PARAMS);
        }
        return currentKey;
    }
}
async function executeGuardedAndRethrow(fn) {
    try {
        return await fn();
    }
    catch (error) {
        assertCanHandle(error, (err) => err instanceof HttpResponseError);
        if (error instanceof FetcherUnauthorizedError) {
            throw new WebFsUnauthorizedError(error.message);
        }
        throw new WebFsUnknownError(error.message);
    }
}
